/**
 * NetSummaryCard — Mode-aware summary card below the hero gradient.
 *
 * Art mode:      "Net this month" + signed dollar value
 * Survival mode: "Burn coverage"  + percentage + progress bar
 *
 * Crossfades between the two states to match hero toggle behavior.
 * Extracted from DashboardHero V6 pill in V7 refactor.
 */

import type { HeroMode } from './DashboardHero';
import { T } from '@/styles/tokens';

const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

interface NetSummaryCardProps {
  mode: HeroMode;
  net: number;          // cents — earned minus spent
  earned: number;       // cents
  burn: number;         // cents — monthly burn target (manual) or actual spend (plaid)
  burnSource?: 'plaid' | 'manual';
  dayOfMonth: number;
  daysInMonth: number;
}

export function NetSummaryCard({ mode, net, earned, burn, burnSource = 'manual', dayOfMonth, daysInMonth }: NetSummaryCardProps) {
  const isArt = mode === 'art';

  // ── Art mode values ──
  const netDollars = Math.round(Math.abs(net) / 100);
  const isPositive = net > 0;
  const isNegative = net < 0;
  const netPrefix = isPositive ? '+' : isNegative ? '-' : '+';
  const netColor = isPositive ? T.primary : isNegative ? '#F87171' : T.textMuted;

  // ── Survival mode values ──
  const pct = burn > 0 ? Math.round((earned / burn) * 100) : earned > 0 ? 100 : 0;

  const layerStyle = (visible: boolean): React.CSSProperties => ({
    position: 'absolute',
    inset: 0,
    opacity: visible ? 1 : 0,
    transition: `opacity 0.3s ${EASE}`,
    pointerEvents: visible ? 'auto' : 'none',
    display: 'flex',
    alignItems: 'center',
    padding: '0 18px',
  });

  return (
    <div style={{
      background: T.white,
      borderRadius: '14px 14px 0 0',
      height: 56,
      position: 'relative',
      overflow: 'hidden',
      marginBottom: 0,
    }}>
      {/* Art — Net this month */}
      <div style={{
        ...layerStyle(isArt),
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: isNegative ? 'rgba(248,113,113,0.10)' : T.primarySubtle,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke={isNegative ? '#F87171' : T.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
            </svg>
          </div>
          <span style={{
            fontSize: 13, fontWeight: 500, color: T.textSecondary, fontFamily: T.fontFamily,
          }}>
            Net this month
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontSize: 16, fontWeight: 700, color: netColor,
            fontFamily: T.fontFamily, fontVariantNumeric: 'tabular-nums',
          }}>
            {netPrefix}${netDollars.toLocaleString()}
          </span>
          {/* 18px spacer aligns with chevron column in Earned/Spend below */}
          <div style={{ width: 18, flexShrink: 0 }} />
        </div>
      </div>

      {/* Survival — Burn coverage
           Uses same centered-row layout as Art so icon/label stays at
           identical Y during crossfade. Progress bar is absolute-bottom. */}
      <div style={{
        ...layerStyle(!isArt),
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: pct >= 100 ? T.primarySubtle : 'rgba(61,171,165,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke={pct >= 100 ? T.primary : T.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
              <path d="M12 6v6l4 2" />
            </svg>
          </div>
          <span style={{
            fontSize: 13, fontWeight: 500, color: T.textSecondary, fontFamily: T.fontFamily,
          }}>
            Burn coverage
          </span>
        </div>
        <span style={{
          fontSize: 13, fontWeight: 600,
          color: pct >= 100 ? T.primary : T.textSecondary,
          fontFamily: T.fontFamily,
        }}>
          {pct}% · Day {dayOfMonth}/{daysInMonth}
        </span>
        {/* Progress bar pinned to bottom of card */}
        <div style={{
          position: 'absolute', bottom: 8, left: 60, right: 18,
          height: 3, borderRadius: 2, background: T.border, overflow: 'hidden',
        }}>
          <div style={{
            height: '100%',
            width: `${Math.min(pct, 100)}%`,
            borderRadius: 2,
            background: pct >= 100 ? T.primary : T.primary,
            transition: `width 0.4s ${EASE}`,
          }} />
        </div>
      </div>
    </div>
  );
}
